/**
 * Share Report Dialog Component (Reports v2)
 *
 * Two layers of sharing for an AnalyseDashboard:
 *
 *   1. Coarse `shared_with` setting (private | company | company_and_customers)
 *      saved via POST /reporting/dashboards/{id}/share/.
 *   2. Per-user shares (ReportShare rows) listed/created/deleted via
 *      /reporting/shares/?dashboard={id}.
 *
 * The dialog handles autocomplete of users via coreAPI.getUserProfiles
 * with a `search` query. Each share row supports a `can_edit` toggle and
 * a delete button.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../../components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Loader2, Search, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  reportingAPI,
  type AnalyseDashboard,
  type ReportShare,
} from '../../../lib/api/reporting';
import { coreAPI } from '../../../lib/api/core';
import type { UserProfile } from '../../../lib/api/types';

type SharedWith = AnalyseDashboard['shared_with'];

interface ShareReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
  saving: boolean;
  /**
   * Persist the coarse `shared_with` setting via the dashboards/share/
   * endpoint. The parent should refresh the report list afterwards.
   */
  onSaveSharedWith: (sharedWith: SharedWith) => Promise<void> | void;
}

interface ShareRowDisplay {
  share: ReportShare;
  display_name: string;
  email: string;
}

export function ShareReportDialog({
  open,
  onOpenChange,
  report,
  saving,
  onSaveSharedWith,
}: ShareReportDialogProps) {
  const [sharedWith, setSharedWith] = useState<SharedWith>('company');
  const [shares, setShares] = useState<ShareRowDisplay[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  // User search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<number | null>(null);

  // Pre-fill sharing mode + load existing shares whenever dialog opens
  useEffect(() => {
    if (!report || !open) return;
    setSharedWith(report.shared_with || 'company');
    void loadShares(report.id);
    setSearchQuery('');
    setSearchResults([]);
  }, [report, open]);

  const loadShares = useCallback(async (dashboardId: number) => {
    setLoadingShares(true);
    try {
      const response = await reportingAPI.listShares(dashboardId);
      const rows: ShareRowDisplay[] = (response.data.results || []).map((s) => ({
        share: s,
        display_name: '',
        email: '',
      }));

      // Hydrate display info for user-type targets (best effort)
      const userTargets = rows.filter((r) => r.share.target_type === 'user');
      if (userTargets.length > 0) {
        try {
          const profilesRes = await coreAPI.getUserProfiles();
          const lookup = new Map<number, UserProfile>();
          (profilesRes.data.results || []).forEach((p) => lookup.set(p.user.id, p));
          userTargets.forEach((row) => {
            const match = lookup.get(row.share.target_id);
            if (match) {
              row.display_name = `${match.user.first_name || ''} ${match.user.last_name || ''}`.trim() || match.user.email;
              row.email = match.user.email;
            }
          });
        } catch (err: unknown) {
          // Non-fatal — just leave name/email blank
          console.warn('Failed to hydrate share user names:', err);
        }
      }
      setShares(rows);
    } catch (err: unknown) {
      console.error('Failed to load shares:', err);
      toast.error('Failed to load shared users');
    } finally {
      setLoadingShares(false);
    }
  }, []);

  // Debounced user search
  useEffect(() => {
    if (!open || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      setSearching(true);
      try {
        const response = await coreAPI.getUserProfiles({ search: searchQuery.trim(), page_size: 10 });
        setSearchResults(response.data.results || []);
      } catch (err: unknown) {
        console.error('User search failed:', err);
        toast.error('User search failed');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchQuery, open]);

  const existingUserIds = useMemo(
    () => new Set(shares.filter((s) => s.share.target_type === 'user').map((s) => s.share.target_id)),
    [shares],
  );

  const handleAddUser = async (profile: UserProfile) => {
    if (!report) return;
    if (existingUserIds.has(profile.user.id)) {
      toast.info('User already has access');
      return;
    }
    setAdding(profile.user.id);
    try {
      const response = await reportingAPI.createShare({
        dashboard: report.id,
        target_type: 'user',
        target_id: profile.user.id,
        can_edit: false,
      });
      const display_name = `${profile.user.first_name || ''} ${profile.user.last_name || ''}`.trim() || profile.user.email;
      setShares((prev) => [
        ...prev,
        { share: response.data, display_name, email: profile.user.email },
      ]);
      toast.success(`Shared with ${display_name}`);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err: unknown) {
      console.error('Failed to add share:', err);
      toast.error('Failed to share with user');
    } finally {
      setAdding(null);
    }
  };

  const handleToggleEdit = async (row: ShareRowDisplay, canEdit: boolean) => {
    try {
      const response = await reportingAPI.updateShare(row.share.id, { can_edit: canEdit });
      setShares((prev) =>
        prev.map((r) => (r.share.id === row.share.id ? { ...r, share: response.data } : r)),
      );
    } catch (err: unknown) {
      console.error('Failed to update share:', err);
      toast.error('Failed to update share');
    }
  };

  const handleDeleteShare = async (row: ShareRowDisplay) => {
    try {
      await reportingAPI.deleteShare(row.share.id);
      setShares((prev) => prev.filter((r) => r.share.id !== row.share.id));
      toast.success('Removed access');
    } catch (err: unknown) {
      console.error('Failed to delete share:', err);
      toast.error('Failed to remove access');
    }
  };

  const handleSave = async () => {
    if (!report) return;
    if (sharedWith !== report.shared_with) {
      await onSaveSharedWith(sharedWith);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Report</DialogTitle>
          <DialogDescription>
            Choose who can see "{report?.name}". You can also share with specific users
            individually.
          </DialogDescription>
        </DialogHeader>

        {/* Visibility radio group */}
        <div className="space-y-3 py-2">
          <Label className="text-sm font-medium">Visibility</Label>
          <RadioGroup
            value={sharedWith}
            onValueChange={(value) => setSharedWith(value as SharedWith)}
            className="gap-3"
          >
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <RadioGroupItem value="private" id="share-private" className="mt-0.5" />
              <Label htmlFor="share-private" className="flex-1 cursor-pointer space-y-1">
                <div className="font-medium">Private</div>
                <div className="text-xs text-muted-foreground">Only you can see this report.</div>
              </Label>
            </div>
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <RadioGroupItem value="company" id="share-company" className="mt-0.5" />
              <Label htmlFor="share-company" className="flex-1 cursor-pointer space-y-1">
                <div className="font-medium">Company</div>
                <div className="text-xs text-muted-foreground">All users in your company can see this report.</div>
              </Label>
            </div>
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <RadioGroupItem value="company_and_customers" id="share-customers" className="mt-0.5" />
              <Label htmlFor="share-customers" className="flex-1 cursor-pointer space-y-1">
                <div className="font-medium">Company & Customers</div>
                <div className="text-xs text-muted-foreground">
                  Visible to your company users and to your customer portal users.
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Per-user shares */}
        <div className="space-y-3 pt-3 border-t">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Shared With (Specific Users)</Label>
          </div>

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Search results */}
          {searchQuery.trim() && (
            <div className="border rounded-md max-h-40 overflow-y-auto">
              {searching ? (
                <div className="flex items-center justify-center py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Searching...
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-sm text-muted-foreground py-3 px-3">No users found.</div>
              ) : (
                searchResults.map((profile) => {
                  const display_name = `${profile.user.first_name || ''} ${profile.user.last_name || ''}`.trim() || profile.user.email;
                  const already = existingUserIds.has(profile.user.id);
                  return (
                    <button
                      key={profile.id}
                      type="button"
                      onClick={() => !already && void handleAddUser(profile)}
                      disabled={already || adding === profile.user.id}
                      className="w-full flex items-center justify-between p-2.5 text-left hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed border-b last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{display_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{profile.user.email}</div>
                      </div>
                      {already ? (
                        <span className="text-xs text-muted-foreground">Already shared</span>
                      ) : adding === profile.user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <UserPlus className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* Existing shares table */}
          <div className="border rounded-md">
            {loadingShares ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading shares...
              </div>
            ) : shares.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No individual users shared yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="w-[140px]">Can Edit</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shares.map((row) => (
                    <TableRow key={row.share.id}>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {row.display_name || `User #${row.share.target_id}`}
                        </div>
                        {row.email && (
                          <div className="text-xs text-muted-foreground">{row.email}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row.share.can_edit}
                          onCheckedChange={(checked) => void handleToggleEdit(row, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => void handleDeleteShare(row)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Close
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Visibility
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
